import {
  Coffee,
  Diet,
  Drinking,
  SleepTime as SleepTimeIcon,
  Smoking,
  StressLevel as StressLevelIcon,
  WorkOut,
} from "@becon-team/becon-fe-lib/components";
import { getUserHairLossLevelData, getUserHairLossTypeData } from "@becon-team/becon-fe-lib/utils";
import { ReactElement, ReactNode } from "react";

import {
  NoToMoreThenOne,
  NoToMoreThenThree,
  SleepTime,
  StressLevel,
  UserHealthInquiryKey,
  PreferenceShampoo,
  PreferenceHair,
  NoneToMoreThenThree,
  WashHairCycle,
  YesOrNo,
} from "../services/user.dto";

import type { TFunction } from "i18next";
import { BeconUserTypes, Nullable, Nullish } from "@becon-team/becon-fe-lib/my-types";
import { MyTFunction } from "@becon-team/becon-fe-lib/my-i18n";

export const getUserHairLossTypeAndLevelData = (
  t: MyTFunction,
  hairLossType: BeconUserTypes.HairlossType,
  hairLossLevel: number,
  gender: Nullish<BeconUserTypes.UserGender>,
): {
  text: string;
  icon: Nullable<ReactElement>;
} => {
  switch (hairLossType) {
    case "NONE":
      return {
        text: getUserHairLossTypeData(t, "NONE").text,
        icon: getUserHairLossTypeData(t, "NONE").icon,
      };
    case "THINNING_HAIR":
      return {
        text: getUserHairLossTypeData(t, "THINNING_HAIR").text,
        icon: getUserHairLossTypeData(t, "THINNING_HAIR").icon,
      };
    default:
      return {
        text: getUserHairLossLevelData(t, hairLossLevel, gender || undefined).text,
        icon: getUserHairLossLevelData(t, hairLossLevel, gender || undefined).icon,
      };
  }
};

export const getUserHealthInquiryData = (
  t: TFunction,
  userHealthInquiryKey: UserHealthInquiryKey,
): {
  text: string;
  icon: Nullable<ReactNode>;
} => {
  switch (userHealthInquiryKey) {
    case "diet":
      return {
        text: t("다이어트"),
        icon: <Diet />,
      };
    case "smoking":
      return {
        text: t("흡연"),
        icon: <Smoking />,
      };
    case "coffee":
      return {
        text: t("커피"),
        icon: <Coffee />,
      };
    case "drinking":
      return {
        text: t("음주"),
        icon: <Drinking />,
      };
    case "work_out":
      return {
        text: t("운동"),
        icon: <WorkOut />,
      };
    case "sleep_time":
      return {
        text: t("수면"),
        icon: <SleepTimeIcon />,
      };
    case "stress_level":
      return {
        text: t("스트레스"),
        icon: <StressLevelIcon />,
      };
  }
};

export const getDietOptionText = (t: TFunction, option: YesOrNo): string => {
  switch (option) {
    case "NO":
      return t("안 함");
    case "YES":
      return t("하는 중");
  }
};

export const getSmokingOptionText = (t: TFunction, option: NoToMoreThenOne): string => {
  switch (option) {
    case "NO":
      return t("안 함");
    case "ONE":
      return t("하루 한 갑 미만");
    case "MORE_THAN_ONE":
      return t("하루 한 갑 이상");
  }
};

export const getCoffeeOptionText = (t: TFunction, option: NoToMoreThenThree): string => {
  switch (option) {
    case "NO":
      return t("안 마심");
    case "LESS_THAN_TWO":
      return t("하루 2잔 이하");
    case "MORE_THAN_THREE":
      return t("하루 3잔 이상");
  }
};

export const getDrinkingOptionText = (t: TFunction, option: NoToMoreThenThree): string => {
  switch (option) {
    case "NO":
      return t("안 함");
    case "LESS_THAN_TWO":
      return t("주 2회 이하");
    case "MORE_THAN_THREE":
      return t("주 3회 이상");
  }
};

export const getWorkOutOptionText = (t: TFunction, option: NoToMoreThenThree): string => {
  switch (option) {
    case "NO":
      return t("안 함");
    case "LESS_THAN_TWO":
      return t("주 1-2회");
    case "MORE_THAN_THREE":
      return t("주 3회 이상");
  }
};

export const getSleepTimeOptionText = (t: TFunction, option: SleepTime): string => {
  switch (option) {
    case "LESS_THAN_SIX":
      return t("{{n}}시간 미만", { n: 6 });
    case "MORE_THAN_SIX":
      return t("{{n}}시간 이상", { n: 6 });
  }
};

export const getStressLevelOptionText = (t: TFunction, option: StressLevel): string => {
  switch (option) {
    case "NONE":
      return t("없음");
    case "LOW":
      return t("하");
    case "MIDDLE":
      return t("중");
    case "HIGH":
      return t("상");
  }
};

export const getUserPreferenceShampooOptionText = (t: TFunction, scalpIssue: PreferenceShampoo): string => {
  switch (scalpIssue) {
    case "NONE":
      return t("없어요");
    case "DETERGENCY":
      return t("세정력");
    case "NATURAL":
      return t("천연 성분");
    case "COOLING":
      return t("쿨링감");
    case "HAIRLOSS_FUNCTION":
      return t("탈모 특화");
    case "VEGAN":
      return t("비건 인증");
    case "FRAGRANCE":
      return t("향기");
  }
};

export const getUserPreferenceHairOptionText = (t: TFunction, scalpIssue: PreferenceHair): string => {
  switch (scalpIssue) {
    case "STIFF":
      return t("볼륨감");
    case "SOFT":
      return t("부드러움");
  }
};

export const getUserLifestyleDyeingOptionText = (t: TFunction, lifestyleDyeing: NoneToMoreThenThree): string => {
  switch (lifestyleDyeing) {
    case "NONE":
      return t("안해요");
    case "LESS_THAN_THREE":
      return t("3번 미만으로 해요");
    case "MORE_THAN_THREE":
      return t("3번 이상 해요");
  }
};

export const getUserLifestylePermOptionText = (t: TFunction, lifestylePerm: NoneToMoreThenThree): string => {
  switch (lifestylePerm) {
    case "NONE":
      return t("안해요");
    case "LESS_THAN_THREE":
      return t("3번 미만으로 해요");
    case "MORE_THAN_THREE":
      return t("3번 이상 해요");
  }
};

export const getUserLifestyleWashHairCycleOptionText = (
  t: TFunction,
  lifestyleWashHairCycle: WashHairCycle,
): string => {
  switch (lifestyleWashHairCycle) {
    case "ONCE_IN_A_WHILE":
      return t("2 ~ 3일에 한번");
    case "ONCE_A_DAY":
      return t("하루에 한 번");
    case "TWICE_A_DAY":
      return t("하루에 두 번");
  }
};

export const getUserLifestyleTreatmentProductOptionText = (
  t: TFunction,
  lifestyleTreatmentProduct: YesOrNo,
): string => {
  switch (lifestyleTreatmentProduct) {
    case "YES":
      return t("네");
    case "NO":
      return t("아니요");
  }
};
